import { AsIconButton } from '../../inputs/buttons/as-icon-button.ts';
import styleText from './as-view-set.css?raw';

/** The `name` of each camera button in the view set. */
export type ViewButtonName = 'toggle-perspective' | 'view-fit' | 'view-iso' | 'view-front' | 'view-right' | 'view-top';

/**
 * View controls section component.
 * Renders camera control buttons whose as-button-click events
 * bubble to window for the viewport to handle.
 */
export class TrViewSet extends HTMLElement {
  static readonly tag = 'as-view-set' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      TrViewSet._sheet
    ];

    this.shadowRoot!.innerHTML = /*html*/`
      <div set-id="header-view-set">
        <as-icon-button
          file-name="header/view-perspective.png"
          tooltip-dict-key="tooltip.view.perspective"
          tooltip-default-text="Toggle perspective view"
          name="toggle-perspective"
          toggle
        ></as-icon-button>
        <as-icon-button
          file-name="header/view-fit.png"
          black
          tooltip-dict-key="tooltip.view.fit"
          tooltip-default-text="Fit to screen"
          name="view-fit"
        ></as-icon-button>
        <as-icon-button
          file-name="header/view-iso.png"
          black
          tooltip-dict-key="tooltip.view.iso"
          tooltip-default-text="Isometric view"
          name="view-iso"
        ></as-icon-button>
        <as-icon-button
          file-name="header/view-front.png"
          black
          tooltip-dict-key="tooltip.view.front"
          tooltip-default-text="Toggle front/back view"
          name="view-front"
        ></as-icon-button>
        <as-icon-button
          file-name="header/view-right.png"
          black
          tooltip-dict-key="tooltip.view.right"
          tooltip-default-text="Toggle right/left view"
          name="view-right"
        ></as-icon-button>
        <as-icon-button
          file-name="header/view-top.png"
          black
          tooltip-dict-key="tooltip.view.top"
          tooltip-default-text="Toggle top/bottom view"
          name="view-top"
        ></as-icon-button>
      </div>
    `;
  }

  connectedCallback() {
    window.addEventListener('as-vp-persp-update', ((e: CustomEvent) => {
      const btn = this.shadowRoot!.querySelector('as-icon-button[name="toggle-perspective"]') as AsIconButton | null;
      btn?.forceToggleState(e.detail.perspective);
    }) as EventListener);
  }

  /** Enable or disable the camera buttons named in `names`, or all of them
   *  when `names` is left out. */
  setEnabled(enabled: boolean, names?: ViewButtonName[]): void {
    this.shadowRoot!.querySelectorAll<HTMLElement>('as-icon-button').forEach(btn => {
      if (names && !names.includes(btn.getAttribute('name') as ViewButtonName)) return;
      btn.toggleAttribute('disabled', !enabled);
    });
  }
}

customElements.define(TrViewSet.tag, TrViewSet);
